import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance, plainAxios } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const initialState = {
  postsByWorkspaceId: {}, // { workspaceId: [posts] }
  fetchedAt: {},          // { workspaceId: timestamp }
  feedPosts: [],          // Global discovery feed remains global
  currentPost: null,
  moreFromWorkspace: [],
  isLoading: false,
  error: null,
};

const PostSlice = createSlice({
  name: "post",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPosts: (state, action) => {
      const { workspaceId, posts } = action.payload;
      state.postsByWorkspaceId[String(workspaceId)] = posts;
      state.fetchedAt[String(workspaceId)] = Date.now();
      state.isLoading = false;
    },
    setFeedPosts: (state, action) => {
      state.feedPosts = action.payload;
    },
    setCurrentPost: (state, action) => {
      state.currentPost = action.payload.post;
      state.moreFromWorkspace = action.payload.moreFromWorkspace || [];
    },
    addPost: (state, action) => {
      const post = action.payload;
      const workspaceId = String(post.workspaceId?._id || post.workspaceId);
      if (state.postsByWorkspaceId[workspaceId]) {
        state.postsByWorkspaceId[workspaceId].unshift(post);
      }
    },
    removePost: (state, action) => {
      const { workspaceId, postId } = action.payload;
      if (state.postsByWorkspaceId[workspaceId]) {
        state.postsByWorkspaceId[workspaceId] = state.postsByWorkspaceId[workspaceId].filter(
          post => (post._id || post.id) !== postId
        );
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    togglePostLikeInFeed: (state, action) => {
      const { postId, isLiked, likesCount } = action.payload;
      
      const updatePostsArray = (posts) => {
        if (!posts) return;
        const index = posts.findIndex(p => p._id === postId || p.id === postId);
        if (index !== -1) {
          posts[index].isLiked = isLiked;
          posts[index].likesCount = likesCount;
        }
      };

      updatePostsArray(state.feedPosts);
      
      if (state.currentPost && (state.currentPost._id === postId || state.currentPost.id === postId)) {
        state.currentPost.isLiked = isLiked;
        state.currentPost.likesCount = likesCount;
      }
      
      Object.keys(state.postsByWorkspaceId).forEach(workspaceId => {
        updatePostsArray(state.postsByWorkspaceId[workspaceId]);
      });
    }
  },
  extraReducers: (builder) => {
    // Cross-slice sync: Listen to togglePostLike from DiscoverySlice (Home Feed)
    builder.addCase('discovery/togglePostLike', (state, action) => {
      const { postId, isLiked, likesCount } = action.payload;
      
      const updatePostsArray = (posts) => {
        if (!posts) return;
        const index = posts.findIndex(p => p._id === postId || p.id === postId);
        if (index !== -1) {
          posts[index].isLiked = isLiked;
          posts[index].likesCount = likesCount;
        }
      };

      updatePostsArray(state.feedPosts);
      
      if (state.currentPost && (state.currentPost._id === postId || state.currentPost.id === postId)) {
        state.currentPost.isLiked = isLiked;
        state.currentPost.likesCount = likesCount;
      }
      
      Object.keys(state.postsByWorkspaceId).forEach(workspaceId => {
        updatePostsArray(state.postsByWorkspaceId[workspaceId]);
      });
    });
  }
});

export const { 
  setLoading, setPosts, setFeedPosts, 
  setCurrentPost, addPost, removePost, setError, togglePostLikeInFeed 
} = PostSlice.actions;

/**
 * Fetch all posts for a workspace (Private Management)
 */
export const fetchWorkspacePosts = (workspaceId, force = false) => async (dispatch, getState) => {
  if (!workspaceId) return;

  const state = getState().post;
  const fetchedAt = state.fetchedAt[workspaceId];
  
  if (!force && fetchedAt && Date.now() - fetchedAt < CACHE_DURATION) return;

  dispatch(setLoading(true));
  try {
    const URL = `workspaces/${workspaceId}/showcase`;
    const response = await axiosInstance.get(URL);
    if (response.data.success) {
      dispatch(setPosts({ workspaceId, posts: response.data.data || [] }));
    }
  } catch (error) {
    console.error("fetchWorkspacePosts error:", error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch public posts for a workspace (Discovery GRID)
 */
export const fetchPublicWorkspacePosts = (workspaceId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `discovery/workspaces/${workspaceId}/posts`;
    const response = await plainAxios.get(URL);
    if (response.data.success) {
      // Public view doesn't use the private cache pattern to avoid confusion
      dispatch(setPosts({ workspaceId, posts: response.data.data }));
    }
  } catch (error) {
    console.error("fetchPublicWorkspacePosts error:", error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch public detailed posts for a workspace (Discovery FEED)
 */
export const fetchDetailedWorkspacePosts = (workspaceId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `discovery/workspaces/${workspaceId}/posts/detailed`;
    const response = await axiosInstance.get(URL);
    if (response.data.success) {
      dispatch(setFeedPosts(response.data.data));
    }
  } catch (error) {
    console.error("fetchDetailedWorkspacePosts error:", error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch individual post details (Detailed Poster Single)
 */
export const fetchPostDetails = (postId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `discovery/posts/${postId}`;
    const response = await axiosInstance.get(URL);
    if (response.data.success) {
      dispatch(setCurrentPost(response.data.data));
    }
  } catch (error) {
    console.error("fetchPostDetails error:", error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch Global Discovery Feed (Detailed)
 */
export const fetchDetailedDiscoveryFeed = (page = 1) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `discovery/posts/detailed?page=${page}&limit=10`;
    const response = await axiosInstance.get(URL);
    if (response.data.success) {
      if (page === 1) {
        dispatch(setFeedPosts(response.data.data));
      } else {
        dispatch(setFeedPosts(response.data.data));
      }
    }
  } catch (error) {
    console.error("fetchDetailedDiscoveryFeed error:", error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Create a new post
 */
export const createPost = (workspaceId, formData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const file = formData.get('media');
    const thumbnailFile = formData.get('thumbnail');
    const type = formData.get('type');
    
    let mediaUrl = '';
    let thumbnailUrl = '';

    // 1. Upload Media via Direct S3 Presigned URL
    if (file) {
      const presignRes = await axiosInstance.post('workspaces/me/showcase/presign-url', {
        fileName: file.name || 'upload',
        mimeType: file.type,
        workspaceId
      });
      
      if (!presignRes.data.success) throw new Error("Failed to get presigned URL");
      
      const { uploadUrl, mediaUrl: s3Url } = presignRes.data.data;
      
      // Upload directly to S3 (Importantly using plain axios, not axiosInstance to avoid auth headers being sent to AWS)
      await import('axios').then(axios => axios.default.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        timeout: 0
      }));
      mediaUrl = s3Url;
    }

    // 2. Upload Thumbnail via Direct S3 Presigned URL
    if (thumbnailFile) {
      const presignRes = await axiosInstance.post('workspaces/me/showcase/presign-url', {
        fileName: thumbnailFile.name || 'thumbnail.jpg',
        mimeType: thumbnailFile.type,
        workspaceId
      });
      
      if (!presignRes.data.success) throw new Error("Failed to get presigned URL for thumbnail");
      
      const { uploadUrl, mediaUrl: s3ThumbUrl } = presignRes.data.data;
      
      await import('axios').then(axios => axios.default.put(uploadUrl, thumbnailFile, {
        headers: { 'Content-Type': thumbnailFile.type },
        timeout: 0
      }));
      thumbnailUrl = s3ThumbUrl;
    }

    // 3. Create Post in Database
    const payload = {
      workspaceId,
      serviceId: formData.get('serviceId'),
      type,
      caption: formData.get('caption'),
      mediaUrl,
      thumbnailUrl
    };

    const URL = "workspaces/me/showcase";
    const response = await axiosInstance.post(URL, payload);
    
    if (response.data.success) {
      // Force refresh for this workspace
      dispatch(fetchWorkspacePosts(workspaceId, true));
      showNotification({ message: "Post shared successfully", type: "success" });
      return { success: true };
    }
  } catch (error) {
    console.error("createPost error:", error);
    // Distinguish network errors (no response) from server errors
    let errorMsg;
    if (!error.response) {
      errorMsg = "Connection/Network error. Direct S3 upload failed.";
    } else {
      errorMsg = error.response?.data?.message || "Failed to share post";
    }
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Delete a post
 */
export const deletePost = (workspaceId, postId) => async (dispatch) => {
  try {
    const URL = `workspaces/me/showcase/${postId}`;
    const response = await axiosInstance.delete(URL);
    if (response.data.success) {
      dispatch(removePost({ workspaceId, postId }));
      showNotification({ message: "Post removed", type: "success" });
    }
  } catch (error) {
    showNotification({ message: "Failed to remove post", type: "error" });
  }
};

/**
 * Update a post
 */
export const updatePost = (workspaceId, postId, updateData) => async (dispatch) => {
  try {
    const URL = `workspaces/me/showcase/${postId}`;
    const response = await axiosInstance.patch(URL, updateData);
    if (response.data.success) {
      dispatch(fetchWorkspacePosts(workspaceId, true));
      showNotification({ message: "Post updated", type: "success" });
      return { success: true };
    }
  } catch (error) {
    showNotification({ message: "Failed to update post", type: "error" });
    return { success: false };
  }
};

/**
 * Toggle Post Like (Immersive Feed)
 */
export const togglePostLikeImmersiveAsync = (postId, currentIsLiked, currentLikesCount) => async (dispatch) => {
  const newIsLiked = !currentIsLiked;
  const newLikesCount = newIsLiked ? (currentLikesCount + 1) : Math.max(0, currentLikesCount - 1);

  // Optimistic UI update
  dispatch(togglePostLikeInFeed({ postId, isLiked: newIsLiked, likesCount: newLikesCount }));

  try {
    const response = await axiosInstance.post(`workspaces/showcase/${postId}/react`, {
      action: newIsLiked ? 'add' : 'remove'
    });

    if (!response.data.success) {
      // Revert if failed
      dispatch(togglePostLikeInFeed({ postId, isLiked: currentIsLiked, likesCount: currentLikesCount }));
    }
  } catch (error) {
    // Revert if error
    dispatch(togglePostLikeInFeed({ postId, isLiked: currentIsLiked, likesCount: currentLikesCount }));
  }
};

export default PostSlice.reducer;
